import ArticleHeader from '@/components/ArticleHeader';
import PaywallModal from '@/components/PaywallModal';
import ArticleCard from '@/components/ArticleCard';
import ArticleSkeleton from '@/components/skeletons/ArticleSkeleton';
import ArticleAuthor from '@/components/ArticleAuthor';
import MetaTags from '@/components/MetaTags';
import ContactButton from '@/components/ContactButton';

import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { useCms } from '@/hooks/useCms';
import { useUser } from '@/state/UserContext';
import { trackCustomEvent } from '@/lib/appInsights';
import { TrackingCustomEvents } from '@/constants/constants';
import { formatArticleContent } from '@/utils/formatArticle';

import type { ReactNode } from 'react';
import type { article } from '@/types/articles';
import type { CMSContextType } from '@/types/util.types';

function Article({ isAuthenticated }: { isAuthenticated: boolean }) {
  const { id } = useParams<{ id: string }>();
  const { email, userId } = useUser();
  const { inProgress, instance } = useMsal();
  const { articleDetail, recentArticles, isLoadingArticle, getArticleById } = useCms() as CMSContextType;
  const showPaywall = !articleDetail?.hasAccess || !isAuthenticated;
  const recentArticlesExludingCurrent = recentArticles.filter((article) => article.id !== articleDetail?.id);

  const renderInArticleCta = (): ReactNode => {
    return (
      <aside className='py-5 my-6 bg-[url(/cta-banner-background.svg)] bg-no-repeat bg-cover rounded-md'>
        <p className='font-bold text-center text-white'>Get Access to Premium Content & Market Insights</p>
        <ContactButton className='mx-auto mt-0' />
      </aside>
    );
  };

  /**
   * Handles the login redirect when the user clicks on the paywall button.
   * @param articleDetail - The article object containing details about the article.
   * @param email - The email of the user.
   * @param userId - The user ID of the user.
   */
  const handleLogin = (articleDetail: article, email: string | undefined, userId: string | undefined) => {
    trackCustomEvent(TrackingCustomEvents.click.paywall.login, {
      article_id: articleDetail.id.toString(),
      article_pricing: articleDetail.isPaid ? 'paid' : 'free',
      author_id: articleDetail.author.lookupKey.toString(),
      user_auth_status: isAuthenticated ? 'logged_in' : 'anonymous',
      user_email: email ?? 'anonymous',
      user_id: userId ?? 'anonymous',
    });
    instance.loginRedirect();
  };

  /**
   * Renders the article content with appropriate styling based on authentication and payment status
   * @param article - The article to render
   * @param isAuthenticated - Whether the user is authenticated
   */
  const renderArticleContent = (article: article, isAuthenticated: boolean) => {
    const shouldMaskContent = article.isPaid && (!isAuthenticated || !article.purchased);

    const maskStyle = shouldMaskContent
      ? {
          maskImage: 'linear-gradient(to top, transparent 10%, black 40%, black 100%)',
          WebkitMaskImage: 'linear-gradient(to top, transparent 0%, black 40%, black 100%)',
        }
      : undefined;

    return (
      article.content && (
        <div style={maskStyle}>
          {formatArticleContent(article.content, article.isPaid, isAuthenticated, renderInArticleCta)}
        </div>
      )
    );
  };

  /**
   * On mount, get the article by the ID passed as a URL parameter
   */
  useEffect(() => {
    if (id === undefined) return;

    if (inProgress === InteractionStatus.None) {
      getArticleById(parseInt(id), isAuthenticated);
    }
  }, [id, isAuthenticated, inProgress, getArticleById]);

  console.log((articleDetail?.image?.small || articleDetail?.image?.original) ?? undefined);

  return (
    <>
      {articleDetail && (
        <MetaTags
          title={articleDetail.title}
          description={articleDetail.summary || articleDetail.title}
          imageUrl={(articleDetail?.image?.small || articleDetail?.image?.original) ?? undefined}
        />
      )}

      {isLoadingArticle && <ArticleSkeleton />}

      {!isLoadingArticle && articleDetail && (
        <article>
          <ArticleHeader {...articleDetail} />

          <div className='md:container mx-auto px-4 mt-6 pb-8 md:mt-8 print:mt-0 md:flex md:gap-12 lg:justify-between relative'>
            <div className='flex flex-col gap-6 md:w-full lg:min-w-unset lg:!max-w-[840px]'>
              <ArticleAuthor
                publishedAt={articleDetail.publishedAt}
                image={articleDetail.author.image}
                name={articleDetail.author.name}
                id={articleDetail.author.lookupKey}
                size='large'
              />

              <p className='font-bold text-[1.2rem] mb-1'>{articleDetail.summary}</p>

              {articleDetail.content && (
                <div data-cy='article-content' className='article-content relative'>
                  {renderArticleContent(articleDetail, isAuthenticated)}
                </div>
              )}

              {showPaywall && (
                <PaywallModal
                  isPaid={articleDetail.isPaid}
                  handleCtaClick={() => handleLogin(articleDetail, email, userId)}
                />
              )}
            </div>

            <aside
              className='hidden lg:block md:min-w-[360px] md:max-w-[360px] print:hidden self-start'
              style={{ position: 'sticky', top: isAuthenticated ? '134px' : '94px' }}
            >
              <h2 className='text-2xl mb-4'>Recent Reports</h2>
              <div className='flex flex-col gap-5'>
                {recentArticlesExludingCurrent.slice(0, 3).map((article) => (
                  <ArticleCard article={article} key={article.id} layout='noImage' />
                ))}
              </div>
            </aside>
          </div>
        </article>
      )}
    </>
  );
}

export default Article;
